import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  definirAlvoDoProduto,
  lerAlvosPorProduto,
  lerConfigDoGiro,
  salvarConfigDoGiro,
} from '../../giro';
import type { AlvoDoProduto, ConfigDoGiro, ConfigEditavelDoGiro } from '../../giro';
import { listarProdutos } from '../../produtos';
import type { ProdutoComercial } from '../../produtos';
import { ExcecaoDeCaptura } from './ExcecaoDeCaptura';
import { HorariosDoGiro } from './HorariosDoGiro';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * CONFIGURAÇÕES DO GIRO — o que o 1.0 deixava editar (com versão e motivo), incluindo a GERAÇÃO
 * AUTOMÁTICA: no horário e nos dias marcados o sistema GERA a operação de cada setor (rascunho);
 * publicar para a loja continua sendo decisão de uma pessoa. "Nenhuma configuração fantasma":
 * o que está aqui funciona — o servidor lê de minuto em minuto.
 */

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

type Estado = { fase: 'carregando' } | { fase: 'erro' } | { fase: 'pronto'; atual: ConfigDoGiro };

export function ConfiguracaoDoGiro({ sessao }: { sessao: Sessao }) {
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [v, setV] = useState<ConfigEditavelDoGiro | null>(null);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  const carregar = useCallback(() => {
    setEstado({ fase: 'carregando' });
    void lerConfigDoGiro().then((r) => {
      if (!r.ok) {
        setEstado({ fase: 'erro' });
        return;
      }
      setEstado({ fase: 'pronto', atual: r.dados });
      setV({
        alvo_global: r.dados.alvo_global,
        estoque_minimo: r.dados.estoque_minimo,
        bloqueia_inovapay: r.dados.bloqueia_inovapay,
        foto_aleatoria: r.dados.foto_aleatoria,
        fotos_fixa_por_missao: r.dados.fotos_fixa_por_missao,
        divergencia_sla_horas: r.dados.divergencia_sla_horas,
        geracao_automatica: r.dados.geracao_automatica,
        geracao_horario: r.dados.geracao_horario,
        geracao_dias: r.dados.geracao_dias,
        tempo_meta_min: r.dados.tempo_meta_min ?? 0,
        // Regra fixa (dono, 01/10): controlados nunca entram — a chave não existe mais na tela.
        incluir_controlados: false,
        alvo_automatico: r.dados.alvo_automatico ?? false,
        fora_do_giro_nomes: r.dados.fora_do_giro_nomes ?? [],
      });
    });
  }, []);
  useEffect(carregar, [carregar]);

  if (estado.fase === 'erro')
    return (
      <section className="cv gr">
        <SubnavDoGiro atual="giro-config" />
        <div className="cv-corpo">
          <ErroDeArea oQue="a configuração do Giro" aoTentar={carregar} />
        </div>
      </section>
    );
  const atual = estado.fase === 'pronto' ? estado.atual : null;
  const mudou =
    atual !== null &&
    v !== null &&
    (Object.keys(v) as (keyof ConfigEditavelDoGiro)[]).some(
      (k) => JSON.stringify(v[k]) !== JSON.stringify(atual[k]),
    );
  const muda = <K extends keyof ConfigEditavelDoGiro>(k: K, valor: ConfigEditavelDoGiro[K]) =>
    setV((x) => (x ? { ...x, [k]: valor } : x));

  const salvar = async () => {
    if (!v || salvando) return;
    setSalvando(true);
    setAviso(null);
    const r = await salvarConfigDoGiro(sessao, v, motivo);
    setSalvando(false);
    if (r.tipo === 'confirmada') {
      setMotivo('');
      setAviso({ tom: 'ok', texto: `Salvo como versão ${String(r.dados.versao)}.` });
      carregar();
    } else
      setAviso({
        tom: 'erro',
        texto:
          r.tipo === 'recusada'
            ? r.mensagem
            : 'Não deu para confirmar se salvou. Recarregue a página e confira a versão.',
      });
  };

  return (
    <section className="cv gr">
      <SubnavDoGiro
        atual="giro-config"
        direita={atual ? `versão ${String(atual.versao)} vigente` : undefined}
      />
      <div className="cv-corpo">
        <div className="cv-topo">
          <div className="cv-titulo cv-titulo-2l">
            <h1>Configurações do Giro</h1>
            <span>Cada mudança vira uma versão nova, com motivo · nada é reescrito para trás</span>
          </div>
        </div>

        {!v ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <div className="gc-grade">
            <div className="cv-cartao gc-cartao">
              <h2>
                Geração automática
                <Ajuda rotulo="geração automática">
                  Ligado, o sistema monta sozinho o Giro de cada setor no horário e nos dias
                  marcados. Ele fica como rascunho: alguém ainda precisa publicar para a equipe ver.
                </Ajuda>
              </h2>
              <p className="gc-nota">
                A partir do horário, nos dias marcados, o sistema <b>gera</b> a operação de cada
                setor (rascunho). <b>Publicar</b> para a loja continua sendo de uma pessoa. Setor
                que já tem operação no dia — ou que o gestor cancelou — não é gerado de novo. Roda
                em nome de quem salvou a versão vigente desta configuração.
              </p>
              <label className="gc-chave">
                <input
                  type="checkbox"
                  checked={v.geracao_automatica}
                  onChange={(e) => muda('geracao_automatica', e.target.checked)}
                />
                <span>Gerar sozinho</span>
              </label>
              <div className="gc-linha">
                <label className="gc-campo">
                  <span>
                    Horário
                    <Ajuda rotulo="o horário">
                      A hora em que o sistema monta o Giro do dia sozinho (horário de Brasília).
                    </Ajuda>
                  </span>
                  <input
                    type="time"
                    value={v.geracao_horario}
                    onChange={(e) => muda('geracao_horario', e.target.value)}
                    disabled={!v.geracao_automatica}
                  />
                </label>
                <div className="gc-campo">
                  <span>Dias</span>
                  <div className="gc-dias" role="group" aria-label="Dias da semana">
                    {DIAS.map((d, i) => {
                      // Domingo nunca tem Giro (dono, 01/10): nem aparece para marcar.
                      if (i === 0) return null;
                      const on = v.geracao_dias.includes(i);
                      return (
                        <button
                          key={d}
                          type="button"
                          aria-pressed={on}
                          disabled={!v.geracao_automatica}
                          className={`gc-dia${on ? ' gc-dia-on' : ''}`}
                          onClick={() =>
                            muda(
                              'geracao_dias',
                              on
                                ? v.geracao_dias.filter((x) => x !== i)
                                : [...v.geracao_dias, i].sort((a, b) => a - b),
                            )
                          }
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  <em className="gc-nota">
                    Domingo não tem Giro: a segunda-feira pega as vendas de sábado e domingo.
                  </em>
                </div>
              </div>
            </div>

            <div className="cv-cartao gc-cartao">
              <h2>
                O que entra no Giro
                <Ajuda rotulo="o que entra no Giro">
                  As regras fixas de quem entra e quem nunca entra no Giro. Na caixa abaixo dá para
                  tirar mais produtos pelo nome.
                </Ajuda>
              </h2>
              <p className="gc-nota">
                <b>Regra fixa:</b> entram só os produtos <b>vendidos no dia anterior</b> (os que
                mais venderam primeiro). Nenhum produto sem venda entra. <b>Nunca entram</b>:
                controlados (ético ou genérico — também pela molécula, quando o HOS classifica só
                algumas dosagens como controlado), antibióticos, déficit de atenção, Ozempic,
                Mounjaro e coisas de geladeira (insulinas, canetas, vacinas, enoxaparina,
                Benzetacil). Agulha e seringa de insulina ficam no Giro. Isso não depende de nenhuma
                chave.
              </p>
              <label className="gc-campo">
                <span>
                  Outros nomes que <b>nunca entram</b> (um por linha · casa em qualquer parte do
                  nome)
                </span>
                <textarea
                  rows={4}
                  maxLength={6000}
                  value={v.fora_do_giro_nomes.join('\n')}
                  onChange={(e) => muda('fora_do_giro_nomes', e.target.value.split('\n'))}
                  placeholder={'Ex.:\nSUPOSITORIO DE GLICERINA\nXALATAN'}
                  aria-label="Nomes que nunca entram no Giro"
                />
              </label>
            </div>

            <div className="cv-cartao gc-cartao">
              <h2>
                Contagem e reposição
                <Ajuda rotulo="contagem e reposição">
                  Quanto deixar na prateleira, quem entra, prazo para resolver divergência, fotos e
                  se o Giro trava as vendas do balconista.
                </Ajuda>
              </h2>
              <div className="gc-alvo-modo">
                <span className="gc-rotulo">
                  Alvo de exposição
                  <Ajuda rotulo="alvo de exposição">
                    Quantas unidades de cada produto devem ficar na prateleira. O sistema manda
                    repor até chegar nesse número (se tiver no estoque). Manual: um número para
                    todos. Automático: cada produto usa o melhor dia de venda do mês anterior.
                  </Ajuda>
                </span>
                <div className="cv-segmento" role="tablist" aria-label="Alvo de exposição">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!v.alvo_automatico}
                    className={!v.alvo_automatico ? 'cv-sel' : ''}
                    onClick={() => muda('alvo_automatico', false)}
                  >
                    Manual
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={v.alvo_automatico}
                    className={v.alvo_automatico ? 'cv-sel' : ''}
                    onClick={() => muda('alvo_automatico', true)}
                  >
                    Automático
                  </button>
                </div>
                <p className="cv-cinza cv-peq">
                  {v.alvo_automatico ? (
                    <>
                      <b>Estoque de segurança:</b> cada produto usa o{' '}
                      <b>maior dia de venda do mês anterior</b> — o mesmo número do Giro MAX (vendeu
                      15 no melhor dia → alvo 15). Muda sozinho todo mês, mas{' '}
                      <b>nunca fica abaixo do mínimo</b> abaixo (melhor dia 3 → alvo 6). Exceção do
                      produto (&quot;não cabe&quot; confirmado ou alvo manual) continua valendo.
                    </>
                  ) : (
                    <>
                      Todos os produtos usam o alvo padrão abaixo (ou o do setor), menos as exceções
                      de produto.
                    </>
                  )}
                </p>
              </div>
              <div className="gc-linha">
                <Numero
                  rotulo={
                    v.alvo_automatico
                      ? 'Mínimo (estoque de segurança)'
                      : 'Alvo de exposição (padrão)'
                  }
                  valor={v.alvo_global}
                  aoMudar={(n) => muda('alvo_global', n)}
                  sufixo="un. na prateleira"
                  ajuda={
                    v.alvo_automatico
                      ? 'No automático, nenhum produto fica com alvo menor que este número, mesmo que venda pouco.'
                      : 'Quantas unidades de cada produto devem ficar na prateleira. Setor com alvo próprio e produto com alvo próprio usam o deles.'
                  }
                />
                <Numero
                  rotulo="Estoque mínimo para entrar"
                  valor={v.estoque_minimo}
                  aoMudar={(n) => muda('estoque_minimo', n)}
                  sufixo="un."
                  ajuda="Produto com estoque no HOS abaixo disto fica fora do Giro do dia (não tem o que repor)."
                />
                <Numero
                  rotulo="Prazo da divergência"
                  valor={v.divergencia_sla_horas}
                  aoMudar={(n) => muda('divergencia_sla_horas', n)}
                  sufixo="horas"
                  ajuda="Quanto tempo a equipe tem para resolver uma divergência antes de ela aparecer como atrasada."
                />
                <Numero
                  rotulo="Fotos fixas por missão"
                  valor={v.fotos_fixa_por_missao}
                  aoMudar={(n) => muda('fotos_fixa_por_missao', n)}
                  sufixo="fotos"
                  ajuda="Quantas fotos da prateleira toda missão pede, além das sorteadas. 0 = nenhuma fixa."
                />
                <Numero
                  rotulo="Meta de tempo da missão (0 = sem meta)"
                  valor={v.tempo_meta_min}
                  aoMudar={(n) => muda('tempo_meta_min', n)}
                  sufixo="min"
                  ajuda="Tempo esperado para terminar um bloco. Serve de referência no Painel; não trava nada. 0 = sem meta."
                />
              </div>
              <label className="gc-chave">
                <input
                  type="checkbox"
                  checked={v.foto_aleatoria}
                  onChange={(e) => muda('foto_aleatoria', e.target.checked)}
                />
                <span>
                  <b>Pedir foto</b> em itens sorteados (pela taxa de foto de cada setor) e nas fotos
                  fixas por missão. Desligado, nenhuma foto é pedida — nem as fixas
                </span>
              </label>
              <label className="gc-chave">
                <input
                  type="checkbox"
                  checked={v.bloqueia_inovapay}
                  onChange={(e) => muda('bloqueia_inovapay', e.target.checked)}
                />
                <span>
                  O Giro do dia <b>bloqueia o InovaPay</b>: o balconista só registra venda depois de
                  terminar contagem, reposição e fotos dos blocos dele de hoje. O bloqueio de venda
                  por &quot;não achei&quot; confirmado na auditoria é outro: vale com esta chave
                  ligada ou não, até o gestor liberar em Não cabe &amp; bloqueios
                </span>
              </label>
            </div>

            <div className="cv-cartao gc-cartao gc-salvar">
              <h2>
                Salvar as mudanças acima
                <Ajuda rotulo="salvar as mudanças">
                  Este botão salva Geração automática, O que entra no Giro e Contagem e reposição.
                  As áreas de baixo (alvo por produto, horários, exceção de vendas) têm o botão
                  delas. Toda mudança vira uma versão nova com o seu motivo — dá para ver depois
                  quem mudou o quê.
                </Ajuda>
              </h2>
              <label className="gc-campo">
                <span>
                  Motivo da mudança <b>obrigatório</b>
                </span>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: gerar sozinho às 6h30 de segunda a sábado"
                />
              </label>
              <div className="gc-acoes">
                <button
                  type="button"
                  className="cv-botao cv-botao-primario"
                  disabled={!mudou || motivo.trim().length < 5 || salvando}
                  onClick={() => void salvar()}
                >
                  <Icone nome="check" tamanho={14} traco={1.8} />
                  {salvando ? 'Salvando…' : 'Salvar nova versão'}
                </button>
                <button
                  type="button"
                  className="cv-botao"
                  disabled={!mudou || salvando}
                  onClick={carregar}
                >
                  Descartar
                </button>
                <span className="gc-estado">{mudou ? 'mudanças não salvas' : 'sem mudanças'}</span>
              </div>
              {aviso && (
                <p className={aviso.tom === 'ok' ? 'gc-ok' : 'gc-erro'} role="status">
                  {aviso.texto}
                </p>
              )}
            </div>

            <AlvosPorProduto sessao={sessao} alvoPadrao={atual?.alvo_global ?? 6} />

            {atual && <HorariosDoGiro sessao={sessao} atual={atual} aoSalvar={carregar} />}
            {atual && <ExcecaoDeCaptura sessao={sessao} atual={atual} aoSalvar={carregar} />}
          </div>
        )}
      </div>
    </section>
  );
}

function Numero({
  rotulo,
  valor,
  aoMudar,
  sufixo,
  ajuda,
}: {
  rotulo: string;
  valor: number;
  aoMudar: (n: number) => void;
  sufixo: string;
  ajuda: string;
}) {
  return (
    <label className="gc-campo">
      <span>
        {rotulo}
        <Ajuda rotulo={rotulo.toLowerCase()}>{ajuda}</Ajuda>
      </span>
      <span className="gc-num">
        <input
          type="number"
          inputMode="numeric"
          value={Number.isFinite(valor) ? valor : ''}
          onChange={(e) => aoMudar(Math.trunc(Number(e.target.value)))}
        />
        <em>{sufixo}</em>
      </span>
    </label>
  );
}

/**
 * Exceção de alvo por produto (§27.6 do 1.0): gôndola pequena, item volumoso. Vale a partir da
 * próxima geração; trocar ENCERRA a anterior (a de ontem continua explicando a missão de ontem).
 */
function AlvosPorProduto({ sessao, alvoPadrao }: { sessao: Sessao; alvoPadrao: number }) {
  const [alvos, setAlvos] = useState<readonly AlvoDoProduto[] | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<readonly ProdutoComercial[]>([]);
  const [escolhido, setEscolhido] = useState<{ id: string; nome: string } | null>(null);
  const [alvo, setAlvo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  const carregar = useCallback(() => {
    setFalhou(false);
    void lerAlvosPorProduto().then((r) => (r.ok ? setAlvos(r.dados.alvos) : setFalhou(true)));
  }, []);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    const t = busca.trim();
    if (t.length < 3 || escolhido) {
      setAchados([]);
      return;
    }
    const id = setTimeout(() => {
      void listarProdutos({ filtro: 'todos', busca: t, grupo: '', pagina: 1 }).then(
        (r) => r.ok && setAchados(r.dados.itens.slice(0, 6)),
      );
    }, 250);
    return () => clearTimeout(id);
  }, [busca, escolhido]);

  const gravar = async (productId: string, valor: number | null, porque: string) => {
    setEnviando(true);
    setAviso(null);
    const r = await definirAlvoDoProduto(sessao, productId, valor, porque);
    setEnviando(false);
    if (r.tipo === 'confirmada') {
      setAviso({
        tom: 'ok',
        texto:
          valor === null ? 'Exceção retirada.' : 'Alvo salvo — vale a partir da próxima geração.',
      });
      setEscolhido(null);
      setBusca('');
      setAlvo('');
      setMotivo('');
      carregar();
    } else
      setAviso({
        tom: 'erro',
        texto:
          r.tipo === 'recusada'
            ? r.mensagem
            : 'Não deu para confirmar se salvou. Recarregue e confira a lista.',
      });
  };
  const n = Number(alvo);
  const pronto =
    !!escolhido &&
    alvo !== '' &&
    Number.isInteger(n) &&
    n >= 0 &&
    n <= 999 &&
    motivo.trim().length >= 3;

  return (
    <div className="cv-cartao gc-cartao gc-alvos">
      <h2>
        Alvo por produto
        <Ajuda rotulo="alvo por produto">
          Para um produto específico que precisa de um número diferente na prateleira (gôndola
          pequena, item grande). Ele passa a usar este alvo no lugar do padrão a partir da próxima
          geração.
        </Ajuda>
      </h2>
      <p className="gc-nota">
        Produto com espaço diferente na prateleira (gôndola pequena, item volumoso) ganha alvo
        próprio no lugar do padrão ({alvoPadrao} un.). Vale a partir da próxima geração.
      </p>
      {falhou ? (
        <ErroDeArea oQue="os alvos por produto" aoTentar={carregar} />
      ) : !alvos ? (
        <div className="cv-carregando" aria-busy="true" />
      ) : alvos.length === 0 ? (
        <p className="gc-vazio">Nenhum produto com alvo próprio.</p>
      ) : (
        <ul className="gc-alvos-lista">
          {alvos.map((a) => (
            <li key={a.id}>
              <div>
                <b>{a.produto}</b>
                <span>
                  {a.motivo} · desde {a.desde.split('-').reverse().slice(0, 2).join('/')}
                  {a.por ? ` · ${a.por}` : ''}
                </span>
              </div>
              <strong>{a.alvo} un.</strong>
              <button
                type="button"
                className="cv-botao"
                disabled={enviando}
                onClick={() => void gravar(a.productId, null, 'voltou ao alvo do setor')}
              >
                Tirar
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="gc-alvos-novo">
        <label className="gc-campo gc-alvos-busca">
          <span>Produto</span>
          {escolhido ? (
            <span className="gc-escolhido">
              {escolhido.nome}
              <button
                type="button"
                aria-label="Trocar produto"
                onClick={() => {
                  setEscolhido(null);
                  setBusca('');
                }}
              >
                <Icone nome="x" tamanho={12} traco={2} />
              </button>
            </span>
          ) : (
            <input
              value={busca}
              placeholder="Nome, código ou EAN (3 letras ou mais)"
              onChange={(e) => setBusca(e.target.value)}
            />
          )}
          {achados.length > 0 && (
            <ul className="gc-achados" role="listbox">
              {achados.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => {
                      setEscolhido({ id: p.id, nome: p.descricao });
                      setAchados([]);
                    }}
                  >
                    {p.descricao}
                    {p.codigo ? <em> · {p.codigo}</em> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>
        <label className="gc-campo">
          <span>Alvo</span>
          <span className="gc-num">
            <input
              inputMode="numeric"
              value={alvo}
              onChange={(e) => setAlvo(e.target.value.replace(/\D/g, '').slice(0, 3))}
            />
            <em>un.</em>
          </span>
        </label>
        <label className="gc-campo gc-alvos-motivo">
          <span>Motivo</span>
          <input
            value={motivo}
            maxLength={500}
            placeholder="Ex.: gôndola pequena"
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="cv-botao cv-botao-primario"
          disabled={!pronto || enviando}
          onClick={() => escolhido && void gravar(escolhido.id, n, motivo.trim())}
        >
          {enviando ? 'Salvando…' : 'Salvar alvo'}
        </button>
      </div>
      {aviso && (
        <p className={aviso.tom === 'ok' ? 'gc-ok' : 'gc-erro'} role="status">
          {aviso.texto}
        </p>
      )}
    </div>
  );
}
